import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useMessagingService } from '../../../../apis/messaging/useMessagingService';
import {
  MessageListResponse,
  ConversationListResponse,
  SendMessagePayload,
  Message,
  GroupListResponse,
  GroupMessageListResponse,
  SendGroupMessagePayload,
  CreateGroupPayload,
} from '../../../../apis/messaging/messaging.interfaces';
import { MessagingAPI } from '../../../../apis/messaging/messaging.api';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getAllMessagesKey = 'get-all-messages';
export const getDirectMessagesKey = 'get-direct-messages';
export const getBroadcastMessagesKey = 'get-broadcast-messages';
export const getConversationsKey = 'get-conversations';

// Get all messages hook
export const useGetAllMessagesHook = (
  params: { page?: number; limit?: number },
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const { getAllMessages } = useMessagingService();

  const queryKey = [getAllMessagesKey, params];

  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () =>
      getAllMessages({ ...params, page: params.page ?? 1, limit: params.limit ?? 20 }),
    enabled: options?.enabled !== undefined ? options.enabled : true,
    ...options,
  });
};

// Get direct messages hook
export const useGetDirectMessagesHook = (
  receiver_id: string,
  params: { page?: number; limit?: number },
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const { getDirectMessages } = useMessagingService();

  const queryKey = [getDirectMessagesKey, receiver_id, params];

  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () =>
      getDirectMessages(receiver_id, {
        ...params,
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      }),
    enabled: options?.enabled !== undefined ? options.enabled : !!receiver_id,
    ...options,
  });
};

// Get broadcast messages hook
export const useGetBroadcastMessagesHook = (
  class_id?: string,
  params: { page?: number; limit?: number; instituteId?: string } = {},
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const { getBroadcastMessages } = useMessagingService();

  const queryKey = [getBroadcastMessagesKey, class_id, params];

  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () =>
      getBroadcastMessages(class_id, {
        ...params,
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      }),
    enabled: options?.enabled !== undefined ? options.enabled : true,
    ...options,
  });
};

// Get conversations hook
export const useGetConversationsHook = (
  params: { page?: number; limit?: number },
  options?: UseQueryOptions<ConversationListResponse, TQueryError>
): UseQueryResult<ConversationListResponse, TQueryError> => {
  const { getConversations } = useMessagingService();

  const queryKey = [getConversationsKey, params];

  return useQuery<ConversationListResponse, TQueryError>({
    queryKey,
    queryFn: () =>
      getConversations({ ...params, page: params.page ?? 1, limit: params.limit ?? 20 }),
    enabled: options?.enabled !== undefined ? options.enabled : true,
    ...options,
  });
};

// Send direct message mutation hook
export const useSendDirectMessageHook = (
  options?: UseMutationOptions<
    Message,
    TMutationError,
    SendMessagePayload & { sender_id?: string },
    unknown
  >
) => {
  const { sendDirectMessage } = useMessagingService();

  return useMutation({
    mutationFn: (payload: SendMessagePayload & { sender_id?: string }) =>
      sendDirectMessage(payload),
    ...options,
  });
};

// Send broadcast message mutation hook
export const useSendBroadcastMessageHook = (
  options?: UseMutationOptions<
    Message,
    TMutationError,
    any & { sender_id?: string },
    unknown
  >
) => {
  const { sendBroadcastMessage } = useMessagingService();

  return useMutation({
    mutationFn: (payload: any & { sender_id?: string }) =>
      sendBroadcastMessage(payload),
    ...options,
  });
};

// Mark messages as read mutation hook
export const useMarkMessagesAsReadHook = (
  options?: UseMutationOptions<
    { success: boolean; updated_count: number },
    TMutationError,
    string[],
    unknown
  >
) => {
  const { markMessagesAsRead } = useMessagingService();

  return useMutation({
    mutationFn: (message_ids: string[]) => markMessagesAsRead(message_ids),
    ...options,
  });
};

// Main hook that exposes messaging service methods
export const useMessagingHook = () => {
  return useMessagingService();
};

// ─── Group Chat Hooks ────────────────────────────────────────────────

export const getGroupsKey = 'get-groups';
export const getGroupMessagesKey = 'get-group-messages';

export const useGetGroupsHook = (
  params: { page?: number; limit?: number } = {},
  options?: UseQueryOptions<GroupListResponse, TQueryError>
): UseQueryResult<GroupListResponse, TQueryError> => {
  const queryKey = [getGroupsKey, params];

  return useQuery<GroupListResponse, TQueryError>({
    queryKey,
    queryFn: () => MessagingAPI.getGroups(params.page ?? 1, params.limit ?? 20),
    enabled: options?.enabled !== undefined ? options.enabled : true,
    ...options,
  });
};

export const useGetGroupMessagesHook = (
  groupId: string | null,
  params: { page?: number; limit?: number } = {},
  options?: UseQueryOptions<GroupMessageListResponse, TQueryError>
): UseQueryResult<GroupMessageListResponse, TQueryError> => {
  const queryKey = [getGroupMessagesKey, groupId, params];

  return useQuery<GroupMessageListResponse, TQueryError>({
    queryKey,
    queryFn: () =>
      MessagingAPI.getGroupMessages(groupId!, params.page ?? 1, params.limit ?? 50),
    enabled: options?.enabled !== undefined ? options.enabled : !!groupId,
    ...options,
  });
};

export const useSendGroupMessageHook = (
  options?: UseMutationOptions<Message, TMutationError, SendGroupMessagePayload, unknown>
) => {
  return useMutation({
    mutationFn: (payload: SendGroupMessagePayload) =>
      MessagingAPI.sendGroupMessage(payload),
    ...options,
  });
};

export const useCreateGroupHook = (
  options?: UseMutationOptions<
    { group: any; members: any[] },
    TMutationError,
    CreateGroupPayload,
    unknown
  >
) => {
  return useMutation({
    mutationFn: (payload: CreateGroupPayload) => MessagingAPI.createGroup(payload),
    ...options,
  });
};
